/**
 * Helpers de lotería alineados con el modelo Flutter (LotteryInfoResponse).
 */

/**
 * Lotería "Próximamente" (GET /lottery/results/coming_soon/): se muestra pero NO se puede comprar.
 */
export function isLotteryComingSoon(lottery) {
  if (!lottery || typeof lottery !== 'object') return false;
  const raw = lottery.coming_soon ?? lottery.comingSoon;
  if (typeof raw === 'boolean') return raw;
  const s = String(raw ?? '')
    .trim()
    .toLowerCase();
  return s === 'true' || s === '1' || s === 'yes';
}

export function isLotteryExtra(lottery) {
  if (!lottery || typeof lottery !== 'object') return false;
  const raw = lottery.is_extra ?? lottery.isExtra;
  if (typeof raw === 'boolean') return raw;
  const s = String(raw ?? '')
    .trim()
    .toLowerCase();
  return s === 'true' || s === '1' || s === 'yes';
}

export function getExtraDrawDate(lottery) {
  if (!lottery) return null;
  const raw = lottery.extra_draw_date ?? lottery.extraDrawDate;
  if (raw == null) return null;
  const s = String(raw).trim();
  return s || null;
}

/** Próxima fecha/hora de sorteo (ordinario o extra). */
export function getLotteryDrawDateTime(lottery) {
  if (!lottery) return null;
  const raw =
    lottery.next_draw_datetime ??
    lottery.nextDrawDateTime ??
    lottery.extra_draw_date ??
    lottery.extraDrawDate ??
    lottery.draw_date ??
    lottery.drawDate;
  if (raw == null) return null;
  const s = String(raw).trim();
  return s || null;
}

/**
 * Normaliza a YYYY-MM-DD para el campo `draw_date` de create_bet.
 * Acepta `yyyy-MM-dd HH:mm:ss`, ISO, etc.
 */
export function toDrawDatePayload(value) {
  if (value == null) return null;
  const s = String(value).trim();
  if (!s) return null;
  const match = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (match) return match[1];
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function getPrizePlanUrl(lottery) {
  const url = lottery?.prize_plan_file ?? lottery?.prizePlanFile;
  if (url == null) return null;
  const s = String(url).trim();
  return s || null;
}

/** Abre el PDF del plan de premios en una pestaña nueva (mejor UX móvil que un modal). */
export function openPrizePlanInNewView(lottery) {
  const url = getPrizePlanUrl(lottery);
  if (!url) return false;
  window.open(url, '_blank', 'noopener,noreferrer');
  return true;
}

const WEEKDAYS_ES = [
  'domingo',
  'lunes',
  'martes',
  'miércoles',
  'jueves',
  'viernes',
  'sábado',
];

function parseBettingOpen(raw) {
  if (raw == null) return null;
  if (typeof raw === 'boolean') return raw;
  const s = String(raw).trim().toLowerCase();
  if (s === 'true' || s === '1') return true;
  if (s === 'false' || s === '0') return false;
  return null;
}

function normalizeSpanishWeekday(value) {
  if (value == null) return '';
  return String(value)
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/** Parse `"HH:mm"` o `"HH:mm:ss"` (24 h). */
export function parseHourMinuteColombia(raw) {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s) return null;
  const parts = s.split(':');
  if (!parts.length) return null;
  const h = Number.parseInt(parts[0].trim(), 10);
  if (!Number.isFinite(h) || h < 0 || h > 23) return null;
  let m = 0;
  if (parts.length >= 2) {
    const head = parts[1].trim().split(/[.\s]/)[0];
    m = Number.parseInt(head, 10) || 0;
  }
  if (m < 0 || m > 59) return null;
  return { hour: h, minute: m };
}

/** `"19:00"` → `"7:00 p. m."` (es-CO). */
export function formatClosingTimeDisplay12h(raw) {
  const p = parseHourMinuteColombia(raw);
  if (!p) return String(raw || '').trim();
  const t = new Date(Date.UTC(1970, 0, 1, p.hour, p.minute));
  return new Intl.DateTimeFormat('es-CO', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'UTC',
  }).format(t);
}

function naiveColombiaWallToUtc({ year, month, day, hour, minute, second = 0, ms = 0 }) {
  return new Date(
    Date.UTC(year, month - 1, day, hour, minute, second, ms) + 5 * 60 * 60 * 1000
  );
}

/**
 * `next_draw_datetime` como reloj Colombia (`yyyy-MM-dd HH:mm:ss` sin zona) → UTC.
 * Si el string trae Z u offset ISO, se respeta.
 */
export function parseNextDrawUtcColombia(raw) {
  if (raw == null || String(raw).trim() === '') return null;
  const s = String(raw).trim();
  const hasExplicitZone =
    /Z$/i.test(s) || /[+-]\d{2}:\d{2}$/.test(s) || /[+-]\d{4}$/.test(s);
  if (hasExplicitZone) {
    const t = new Date(s);
    return Number.isNaN(t.getTime()) ? null : t;
  }
  const m = s.match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?(?:\.(\d+))?/
  );
  if (m) {
    const frac = m[7] ? Number(String(m[7]).padEnd(3, '0').slice(0, 3)) : 0;
    return naiveColombiaWallToUtc({
      year: Number(m[1]),
      month: Number(m[2]),
      day: Number(m[3]),
      hour: Number(m[4]),
      minute: Number(m[5]),
      second: Number(m[6] || 0),
      ms: Number.isFinite(frac) ? frac : 0,
    });
  }
  const fallback = new Date(s);
  if (Number.isNaN(fallback.getTime())) return null;
  return naiveColombiaWallToUtc({
    year: fallback.getFullYear(),
    month: fallback.getMonth() + 1,
    day: fallback.getDate(),
    hour: fallback.getHours(),
    minute: fallback.getMinutes(),
    second: fallback.getSeconds(),
  });
}

function bogotaCivilFromUtcInstant(utcDate) {
  const shifted = new Date(utcDate.getTime() - 5 * 60 * 60 * 1000);
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
    hour: shifted.getUTCHours(),
    minute: shifted.getUTCMinutes(),
    second: shifted.getUTCSeconds(),
    weekday: shifted.getUTCDay(),
  };
}

function civilDateValue(civil) {
  return Date.UTC(civil.year, civil.month - 1, civil.day);
}

function matchesLotteryWeekday(drawDayName, civil) {
  if (!drawDayName || !String(drawDayName).trim()) return false;
  const expected = WEEKDAYS_ES[civil.weekday];
  return (
    normalizeSpanishWeekday(drawDayName) === normalizeSpanishWeekday(expected)
  );
}

export function isPastDrawInstantColombia(nextDrawRaw, clockUtc = new Date()) {
  const drawUtc = parseNextDrawUtcColombia(nextDrawRaw);
  if (!drawUtc) return false;
  return !(clockUtc.getTime() < drawUtc.getTime());
}

/**
 * Misma regla que Flutter `isBettingClosedForLottery`.
 * Corte por hora solo el día del sorteo (Colombia).
 */
export function isBettingClosedForLottery(
  nextDrawRaw,
  closingTimeRaw,
  dailyBettingClosesAtRaw,
  { clockUtc = new Date(), drawDayName = null } = {}
) {
  const now = clockUtc instanceof Date ? clockUtc : new Date(clockUtc);
  const drawUtc = parseNextDrawUtcColombia(nextDrawRaw);
  if (drawUtc && !(now.getTime() < drawUtc.getTime())) return true;
  if (!drawUtc) return false;

  const nowCivil = bogotaCivilFromUtcInstant(now);
  const drawCivil = bogotaCivilFromUtcInstant(drawUtc);
  const todayValue = civilDateValue(nowCivil);
  const drawValue = civilDateValue(drawCivil);
  const isDrawDay = todayValue === drawValue;

  const closingParsed = parseHourMinuteColombia(closingTimeRaw);
  const dailyParsed = parseHourMinuteColombia(dailyBettingClosesAtRaw);
  const cutoff = dailyParsed || closingParsed;
  if (!cutoff) return false;

  const minutesNow = nowCivil.hour * 60 + nowCivil.minute;
  const minutesCutoff = cutoff.hour * 60 + cutoff.minute;
  const isPastCutoff = minutesNow >= minutesCutoff;

  if (isDrawDay) return isPastCutoff;

  const daysUntilDraw = Math.round((drawValue - todayValue) / 86400000);
  if (
    daysUntilDraw >= 1 &&
    daysUntilDraw <= 7 &&
    isPastCutoff &&
    matchesLotteryWeekday(drawDayName, nowCivil)
  ) {
    return true;
  }

  return false;
}

export function getEffectiveClosingTime(lottery) {
  if (!lottery) return '';
  const closing = String(lottery.closing_time ?? lottery.closingTime ?? '').trim();
  if (closing) return closing;
  return String(lottery.time ?? '').trim();
}

export function getDailyBettingClosesAt(lottery) {
  if (!lottery) return null;
  const raw = lottery.daily_betting_closes_at ?? lottery.dailyBettingClosesAt;
  if (raw == null) return null;
  const s = String(raw).trim();
  return s || null;
}

/** Cerrado: `betting_open: false` y/o ventana de horario (espejo Flutter). */
export function isLotteryBettingClosed(lottery, clockUtc = new Date()) {
  if (!lottery || typeof lottery !== 'object') return false;
  // Una lotería "Próximamente" nunca está abierta a la venta.
  if (isLotteryComingSoon(lottery)) return true;
  const extra = isLotteryExtra(lottery);
  const closedByTime = isBettingClosedForLottery(
    lottery.next_draw_datetime ?? lottery.nextDrawDateTime,
    getEffectiveClosingTime(lottery),
    getDailyBettingClosesAt(lottery),
    {
      clockUtc,
      drawDayName: extra ? null : lottery.day,
    }
  );
  const bettingOpen = parseBettingOpen(lottery.betting_open ?? lottery.bettingOpen);
  if (bettingOpen != null) {
    return !bettingOpen || closedByTime;
  }
  return closedByTime;
}

export function getClosingScheduleSummary(lottery) {
  const ct = getEffectiveClosingTime(lottery);
  const daily = getDailyBettingClosesAt(lottery);
  const hasDaily = Boolean(daily);
  if (!ct && !hasDaily) {
    return 'Apuestas cerradas o sorteo ya jugado.';
  }
  if (ct && hasDaily) {
    return `Cerrado: hoy del sorteo hasta ${daily} o ${ct}.`;
  }
  if (ct) {
    return `Cerrado: hoy del sorteo después de ${ct}.`;
  }
  return `Cerrado: hoy del sorteo después de ${daily}.`;
}

export function getOpenClosingBadgeText(lottery, clockUtc = new Date()) {
  if (isLotteryBettingClosed(lottery, clockUtc)) return null;
  const nextDraw = lottery?.next_draw_datetime ?? lottery?.nextDrawDateTime;
  const drawUtc = parseNextDrawUtcColombia(nextDraw);
  let hint = getEffectiveClosingTime(lottery);
  if (drawUtc) {
    const nowCivil = bogotaCivilFromUtcInstant(clockUtc);
    const drawCivil = bogotaCivilFromUtcInstant(drawUtc);
    if (civilDateValue(nowCivil) === civilDateValue(drawCivil)) {
      const daily = getDailyBettingClosesAt(lottery);
      if (daily) hint = daily;
    }
  }
  if (!hint) return null;
  return `Cierra ${formatClosingTimeDisplay12h(hint)}`;
}

function formatCivilDateEs(civil, { weekday = 'short', month = 'short' } = {}) {
  const d = new Date(Date.UTC(civil.year, civil.month - 1, civil.day, 12));
  return new Intl.DateTimeFormat('es-CO', {
    weekday,
    day: 'numeric',
    month,
    timeZone: 'UTC',
  }).format(d);
}

/**
 * Fecha (reloj Colombia) del próximo sorteo, legible: `"vie, 3 oct"`.
 * Usa `next_draw_datetime`; si no viene, la fecha extra / draw_date.
 */
export function getDrawDateLabel(lottery, options) {
  const raw = getLotteryDrawDateTime(lottery);
  if (!raw) return null;
  const drawUtc = parseNextDrawUtcColombia(raw);
  if (!drawUtc) return null;
  return formatCivilDateEs(bogotaCivilFromUtcInstant(drawUtc), options);
}

/**
 * "Compra hasta …": día del sorteo + hora de corte de ventas de ese día
 * (misma regla de corte que `isBettingClosedForLottery`: daily || closing_time).
 * Si no hay hora de corte, se usa la hora del sorteo. Solo presentación.
 */
export function getPurchaseDeadlineLabel(lottery) {
  if (!lottery) return null;
  const raw = lottery.next_draw_datetime ?? lottery.nextDrawDateTime ?? getLotteryDrawDateTime(lottery);
  const drawUtc = parseNextDrawUtcColombia(raw);
  if (!drawUtc) return null;
  const civil = bogotaCivilFromUtcInstant(drawUtc);
  const dateText = formatCivilDateEs(civil, { weekday: 'long', month: 'short' });
  const cutoffRaw = getDailyBettingClosesAt(lottery) || getEffectiveClosingTime(lottery);
  const cutoff = parseHourMinuteColombia(cutoffRaw);
  const timeText = cutoff
    ? formatClosingTimeDisplay12h(cutoffRaw)
    : formatClosingTimeDisplay12h(
        `${String(civil.hour).padStart(2, '0')}:${String(civil.minute).padStart(2, '0')}`
      );
  return `${dateText} · ${timeText}`;
}

/** Ítem de carrito: misma regla que Flutter `isCartItemDrawPassed`. */
export function isCartItemBettingClosed(item, clockUtc = new Date()) {
  if (!item) return false;
  const drawEndsAt = item.drawEndsAt ?? item.draw_date ?? item.drawDate ?? null;
  const purchaseClosing = String(item.purchaseClosingTime ?? '').trim();
  if (purchaseClosing) {
    return isBettingClosedForLottery(
      drawEndsAt,
      purchaseClosing,
      item.drawDayClosingTime ?? null,
      { clockUtc }
    );
  }
  return isPastDrawInstantColombia(drawEndsAt, clockUtc);
}
